<template>
  <div class="fcf">
    <div>
      <b-field grouped position="is-centered">
        <b-field expanded>
          <b-input type="text" name="job-number" id="job-number"
            placeholder="Input WFM job number..."
            v-model="jobNumber"
            v-focus
            @keyup.native.enter="$router.push({ name: 'show', params: { jobNumber }})"
          />
        </b-field>
        <button
          @click.prevent="$router.push({ name: 'show', params: { jobNumber }})"
          class="button is-primary">Open form</button>
      </b-field>
    </div>

    <b-loading
      :is-full-page="true"
      :active.sync="isLoading"
    />

    <template v-if="formId">
      <div>
        <hr>
        <div class="level">
          <div class="level-left">
            <b-message v-if="changed" type="is-danger">
              There are unsaved changes in the current form. Don't forget to <a href="" @click.prevent="saveForm">save</a> it.
            </b-message>
            <b-message v-else-if="formId" type="is-info">
              <div>Created: {{formatDate(createdDate)}} by <strong>{{createdBy}}</strong></div>
              <div>Modified: {{formatDate(modifiedDate)}} by <strong>{{modifiedBy}}</strong></div>
            </b-message>
          </div>
          <div
            class="level-right"
            v-if="!editNotAllowed || $auth.check(['admin', 'superadmin'])"
          >

            <form-buttons
              :changed="changed"
              :form-id="formId"
              :form-status="formStatus"
              @delete-clicked="onDeleteClick"
              @save-clicked="saveForm"
              @submit-clicked="onSubmitClick"
              @approve-clicked="onApproveClick"
            />

          </div>
        </div>

        <div class="level" v-if="formId">
          <div class="level-left">
            <div class="form-status" >
              <b-field label="Status" horizontal>
                <b-select placeholder="Status"
                  v-model.number="formStatus"
                  @change.native="onStatusChange"
                  :disabled="!$auth.check(['admin', 'superadmin'])"
                >
                  <option
                      v-for="status in $store.state.formStatuses"
                      :value="status.id"
                      :key="status.id">
                      {{ status.name }}
                  </option>
                </b-select>
              </b-field>
            </div>
          </div>
          <div class="level-right">
            <div class="buttons">
              <button
                @click.prevent="downloadEmail"
                class="button is-outlined">
                <b-icon icon="email"/>
                <span>Download email</span>
              </button>
              <button
                @click.prevent="downloadPdf"
                class="button is-outlined">
                <b-icon icon="file-pdf-box"/>
                <span>PDF</span>
              </button>
            </div>
          </div>
        </div>

        <fcf-main
          :jobData="jobData"
          :edit-not-allowed="editNotAllowed"
          @value-changed="handleValueChange"
          @form-changed="onFormChanged"
          @update-job-data="updateJobData"
        />

        <hr>

        <fcf-kyc
          :kyc-data="kycData"
          :job-number="jobNumber"
          :edit-not-allowed="editNotAllowed"
          @form-changed="onFormChanged"
          @update-kyc-data="updateKycData"
        />

        <hr>

        <fcf-check
          :checksData="checksData"
          :edit-not-allowed="editNotAllowed"
          @form-changed="onFormChanged"
        />

        <div class="level">
          <div class="level-left">
            <b-message v-if="changed" type="is-danger">
              There are unsaved changes in the current form. Don't forget to save it.
            </b-message>
          </div>
          <div class="level-right"
            v-if="!editNotAllowed || $auth.check(['admin', 'superadmin'])"
          >
            <form-buttons
              :changed="changed"
              :form-id="formId"
              :form-status="formStatus"
              @delete-clicked="onDeleteClick"
              @save-clicked="saveForm"
              @submit-clicked="onSubmitClick"
              @approve-clicked="onApproveClick"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'

import FcfMain from '@/components/FcfMain'
import FcfKyc from '@/components/FcfKyc'
import FcfCheck from '@/components/FcfCheck'
import FormButtons from '@/components/FormButtons'

export default {
  name: 'Fcf',
  components: {
    FcfMain,
    FcfKyc,
    FcfCheck,
    FormButtons
  },
  created () {
    if (this.$route.params.jobNumber) {
      this.jobNumber = this.$route.params.jobNumber
      this.getData()
    }
  },
  data () {
    return {
      jobNumber: null,
      createdBy: null,
      createdDate: null,
      modifiedBy: null,
      modifiedDate: null,
      jobData: {},
      kycData: { kycHasOutstanding: true, kycUpdated: false },
      checksData: {},
      formId: null,
      formStatus: 0,
      isLoading: false,
      changed: false
    }
  },
  computed: {
    editNotAllowed () {
      return this.formStatus === 10 || this.formStatus === 1
    }
  },
  methods: {
    getData () {
      this.isLoading = true
      this.axios.get(`fcf/${this.jobNumber}`)
        .then(response => {
          if (response.data.hasOwnProperty('formId')) {
            this.formId = response.data.formId
            this.formStatus = Number(response.data.formStatus)
            this.createdBy = response.data.createdBy
            this.createdDate = response.data.createdDate
            this.modifiedBy = response.data.modifiedBy
            this.modifiedDate = response.data.modifiedDate
            this.$set(this, 'jobData', response.data.jobData)
            this.$set(this, 'kycData', response.data.kycData)
            this.$set(this, 'checksData', response.data.checksData)
            this.isLoading = false
          } else {
            this.createForm()
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.createForm()
            return
          }
          this.$toast.open({
            message: `${error.response.data.message}`,
            type: 'is-danger',
            duration: 10000
          })
          this.isLoading = false
        })
    },
    handleValueChange (edit) {
      this.changed = true
      this.$set(this.jobData, edit.name, edit.value)
    },
    createForm () {
      if (!this.jobNumber) this.jobNumber = this.jobData.jobNumber
      this.axios.post(`fcf/${this.jobNumber}`)
        .then(response => {
          this.formId = response.data.formId
          this.formStatus = Number(response.data.formStatus)
          this.createdBy = response.data.createdBy
          this.createdDate = response.data.createdDate
          this.modifiedBy = response.data.modifiedBy
          this.modifiedDate = response.data.modifiedDate
          this.$set(this, 'jobData', response.data.jobData)
          this.$set(this, 'kycData', response.data.kycData)
          this.$set(this, 'checksData', response.data.checksData)
          this.isLoading = false
          // this.updateForm()
        })
        .catch(error => {
          this.$toast.open({
            message: `${error}`,
            type: 'is-danger',
            duration: 10000
          })
          this.isLoading = false
        })
    },
    saveForm () {
      if (this.formId) {
        this.updateForm()
      } else {
        this.createForm()
      }
    },
    updateForm () {
      this.isLoading = true
      this.axios.put('fcf', {
        formId: this.formId,
        formStatus: this.formStatus,
        jobData: this.jobData,
        kycData: this.kycData,
        checksData: this.checksData
      })
        .then(response => {
          this.updateResponseHandler(response)
        })
        .catch(error => {
          let messageText = error
          if (error.response.data.message) {
            messageText = error.response.data.message
          }

          this.$toast.open({
            message: `${messageText}`,
            type: 'is-danger'
          })
          this.isLoading = false
        })
    },
    updateJobData () {
      this.isLoading = true
      this.axios.get(`wfm/job/${this.jobNumber}`)
        .then(response => {
          this.isLoading = false
          for (const key in response.data) {
            if (this.jobData.hasOwnProperty(key)) {
              this.jobData[key] = response.data[key]
            }
          }
          this.changed = true
          this.$toast.open({
            message: 'File Details updated from WFM job',
            type: 'is-info'
          })
        })
        .catch(error => {
          let messageText = error
          if (error.response.data.message) {
            messageText = error.response.data.message
          }
          this.$toast.open({
            message: `${messageText}`,
            type: 'is-danger'
          })
        })
    },
    updateKycData () {
      this.isLoading = true
      this.axios.get(`fcf-kyc/${this.jobNumber}`)
        .then(response => {
          this.isLoading = false
          this.$set(this.kycData, 'kycFormId', response.data.formId)
          this.$set(this.kycData, 'kycDocuments', response.data.docs)
          this.changed = true
          this.$toast.open({
            message: 'Data from KYC system were updated',
            type: 'is-info'
          })
        })
        .catch(error => {
          let messageText = error
          if (error.response.data.message) {
            messageText = error.response.data.message
          }
          this.$toast.open({
            message: `${messageText}`,
            type: 'is-danger'
          })
          this.isLoading = false
        })
    },
    onFormChanged () {
      this.changed = true
    },
    formatDate (date) {
      if (!date) return null
      return moment(date).fromNow()
    },
    onDeleteClick () {
      this.$dialog.confirm({
        title: 'Deleting form',
        message: 'Are you sure you want to <b>delete</b> form? This action cannot be undone.',
        confirmText: 'Delete Form',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: this.deleteForm
      })
    },
    deleteForm () {
      this.axios.delete(`fcf/${this.jobNumber}`)
        .then(_ => {
          this.$toast.open({
            message: 'Form deleted',
            type: 'is-info'
          })
          this.$router.replace({ name: 'main' })
        })
        .catch(error => {
          this.$toast.open({
            message: `Form wasn't deleted. ${error.response.data.message}`,
            type: 'is-danger'
          })
        })
    },
    onStatusChange (event) {
      if (event.target.value !== this.formStatus) {
        this.changed = true
      }
    },
    createLink (content, filename, fileType) {
      const a = window.document.createElement('a')
      a.href = window.URL.createObjectURL(new Blob([content], {type: fileType}))
      a.download = filename

      document.body.appendChild(a)
      a.click()

      document.body.removeChild(a)
    },
    downloadPdf () {
      this.axios.get(`fcf/${this.jobNumber}/pdf`)
        .then(response => {
          this.createLink(response.data, `fcf${this.jobNumber}.pdf`, 'application/pdf')
        })
        .catch(error => {
          let messageText = error
          if (error.response.data.message) {
            messageText = error.response.data.message
          }
          this.$toast.open({
            message: `${messageText}`,
            type: 'is-danger'
          })
          this.isLoading = false
        })
    },
    downloadEmail () {
      this.axios.get(`fcf/${this.jobNumber}/email`)
        .then(response => {
          this.createLink(response.data, `fcf${this.jobNumber}.eml`, 'message/rfc822')
        })
        .catch(error => {
          let messageText = error
          if (error.response.data.message) {
            messageText = error.response.data.message
          }
          this.$toast.open({
            message: `${messageText}`,
            type: 'is-danger'
          })
          this.isLoading = false
        })
    },
    onSubmitClick () {
      this.$dialog.confirm({
        message: 'Please make sure all available information is filled in. Form will be locked for edits. Continue?',
        onConfirm: () => this.submitForm()
      })
    },
    submitForm () {
      this.isLoading = true
      this.formStatus = 10
      if (this.kycData.kycHasOutstanding) this.formStatus = 1
      this.axios.put('fcf', {
        formId: this.formId,
        formStatus: this.formStatus
      })
        .then(response => {
          this.updateResponseHandler(response)
        })
        .catch(error => {
          let messageText = error
          if (error.response.data.message) {
            messageText = error.response.data.message
          }

          this.$toast.open({
            message: `${messageText}`,
            type: 'is-danger'
          })
          this.isLoading = false
        })
    },
    updateResponseHandler (response) {
      this.formStatus = Number(response.data.formStatus)
      this.createdBy = response.data.createdBy
      this.createdDate = response.data.createdDate
      this.modifiedBy = response.data.modifiedBy
      this.modifiedDate = response.data.modifiedDate
      this.$set(this, 'jobData', response.data.jobData)
      this.$set(this, 'kycData', response.data.kycData)
      this.$set(this, 'checksData', response.data.checksData)
      this.isLoading = false
      this.$toast.open({
        message: 'Saved',
        type: 'is-info'
      })
      this.changed = false
    },
    onApproveClick () {
      this.formStatus = 10
      this.updateForm()
    }
  }
}
</script>

<style scoped>
.fcf {
  margin-top: 2em;
}
</style>
